import { getBracketNumberOfRounds } from './bracketSize'
import { isDefined, type BracketRound } from '@terros/common'

export type GenerateRoundsOptions = {
  startDate: number
  bracketParticipantsCount: number
  seedingRoundDuration?: number // in milliseconds, if not provided, dont create a seeding round
  roundDuration: number
  timeBetweenRounds?: number // in milliseconds, if not provided, 0
}

export const generateRounds = (options: GenerateRoundsOptions): BracketRound[] => {
  const { startDate, bracketParticipantsCount, seedingRoundDuration, roundDuration, timeBetweenRounds = 0 } = options

  const bracketStartDate = seedingRoundDuration ? startDate + seedingRoundDuration + timeBetweenRounds : startDate

  const bracketRounds = generateBracketRounds({
    startDate: bracketStartDate,
    bracketParticipantsCount,
    roundDuration,
    timeBetweenRounds,
  })

  if (seedingRoundDuration) {
    const seedingRound = {
      round: 0,
      startDate,
      endDate: startDate + seedingRoundDuration,
      matches: [],
    }
    return [seedingRound, ...bracketRounds]
  }

  return bracketRounds
}

type GenerateBracketRoundsOptions = Omit<GenerateRoundsOptions, 'seedingRoundDuration'>
export const generateBracketRounds = (options: GenerateBracketRoundsOptions): BracketRound[] => {
  const { startDate, bracketParticipantsCount, roundDuration, timeBetweenRounds } = options
  const numberOfRounds = getBracketNumberOfRounds(bracketParticipantsCount)

  const rounds: BracketRound[] = []

  for (let i = 1; i <= numberOfRounds; i++) {
    const prevRound: BracketRound | undefined = rounds.find((r) => r.round === i - 1)

    const roundStartDate = getRoundStartDate(prevRound, startDate, timeBetweenRounds)

    const round = {
      round: i,
      startDate: roundStartDate,
      endDate: roundStartDate + roundDuration,
      matches: [],
    }
    rounds.push(round)
  }

  return rounds.sort((a, b) => a.round - b.round)
}

const getRoundStartDate = (
  prevRound: BracketRound | undefined,
  startDate: number,
  timeBetweenRounds: number | undefined
): number => {
  if (isDefined(prevRound) && isDefined(timeBetweenRounds)) {
    return prevRound.endDate + timeBetweenRounds
  }
  return startDate
}
